/**
 * Roles + permissions mock adapter.
 *
 * Mirrors `rolesApi` in `../roles.ts` so the `MOCKS_ENABLED ? mock : real`
 * swap is a no-op for callers. Seeds the standard HIS Lite role set and a
 * permission catalogue so `/settings/roles`, the role matrix, and the
 * user-role panels on `/settings/users/:id` are click-through.
 */
import { delay } from "./registry";
import type {
  PermissionResponse,
  RoleResponse,
  UserRolesRequest,
  UserRolesResponse,
} from "../../types/role";

const nowISO = new Date().toISOString();

// [code, labelVi, groupVi]
const PERMISSIONS: PermissionResponse[] = (
  [
    ["patient:read", "Xem bệnh nhân", "Bệnh nhân"],
    ["patient:write", "Sửa bệnh nhân", "Bệnh nhân"],
    ["appointment:read", "Xem lịch hẹn", "Lịch hẹn"],
    ["appointment:write", "Quản lý lịch hẹn", "Lịch hẹn"],
    ["encounter:read", "Xem khám bệnh", "Khám bệnh"],
    ["encounter:write", "Ghi nhận khám bệnh", "Khám bệnh"],
    ["prescription:read", "Xem đơn thuốc", "Đơn thuốc"],
    ["prescription:write", "Kê đơn thuốc", "Đơn thuốc"],
    ["prescription:sign", "Ký đơn thuốc", "Đơn thuốc"],
    ["invoice:read", "Xem hoá đơn", "Thu ngân"],
    ["invoice:write", "Lập hoá đơn", "Thu ngân"],
    ["payment:write", "Thu tiền", "Thu ngân"],
    ["inventory:read", "Xem tồn kho", "Kho thuốc"],
    ["inventory:write", "Quản lý kho", "Kho thuốc"],
    ["report:read", "Xem báo cáo", "Báo cáo"],
    ["settings:read", "Xem cấu hình", "Cấu hình"],
    ["settings:write", "Sửa cấu hình", "Cấu hình"],
    ["staff:manage", "Quản lý nhân sự", "Cấu hình"],
    ["role:manage", "Quản lý phân quyền", "Cấu hình"],
  ] as const
).map(([code, labelVi, groupVi]) => ({
  code,
  labelVi,
  groupVi,
  descriptionEn: null,
}));

const ALL = PERMISSIONS.map((p) => p.code);

// [code, name, description, isSystem, permissionCodes]
const ROLE_SEED: [string, string, string, boolean, string[]][] = [
  ["OWNER", "Chủ phòng khám", "Toàn quyền hệ thống", true, ALL],
  [
    "MANAGER",
    "Quản lý",
    "Vận hành phòng khám, không ký đơn",
    true,
    ALL.filter((c) => c !== "prescription:sign"),
  ],
  [
    "DOCTOR",
    "Bác sĩ",
    "Khám, kê đơn, ký đơn",
    true,
    [
      "patient:read",
      "patient:write",
      "appointment:read",
      "encounter:read",
      "encounter:write",
      "prescription:read",
      "prescription:write",
      "prescription:sign",
      "report:read",
    ],
  ],
  [
    "NURSE",
    "Điều dưỡng",
    "Hỗ trợ khám, không kê đơn",
    true,
    [
      "patient:read",
      "appointment:read",
      "appointment:write",
      "encounter:read",
      "encounter:write",
      "inventory:read",
    ],
  ],
  [
    "RECEPTIONIST",
    "Lễ tân",
    "Tiếp đón, đặt lịch, thu ngân",
    true,
    [
      "patient:read",
      "patient:write",
      "appointment:read",
      "appointment:write",
      "invoice:read",
      "invoice:write",
      "payment:write",
    ],
  ],
];

const ROLES: RoleResponse[] = ROLE_SEED.map(
  ([code, name, description, isSystem, permissionCodes], i) => ({
    id: `rol-${String(i + 1).padStart(4, "0")}`,
    code,
    name,
    description,
    isSystem,
    permissionCodes,
    createdAt: nowISO,
    updatedAt: nowISO,
  })
);

// userId -> role codes
const USER_ROLES: Record<string, string[]> = {
  "usr-0001": ["OWNER"],
  "usr-0002": ["MANAGER"],
  "usr-0003": ["DOCTOR"],
  "usr-0004": ["DOCTOR"],
  "usr-0006": ["NURSE"],
  "usr-0007": ["NURSE"],
  "usr-0008": ["RECEPTIONIST"],
};

function userRolesResponse(userId: string): UserRolesResponse {
  const codes = USER_ROLES[userId] ?? [];
  return {
    userId,
    roles: codes
      .map((code) => ROLES.find((r) => r.code === code))
      .filter((r): r is RoleResponse => !!r)
      .map((r) => ({
        roleId: r.id,
        code: r.code,
        name: r.name,
        isSystem: r.isSystem,
      })),
  };
}

export const mockRoles = {
  list(): Promise<RoleResponse[]> {
    return delay(ROLES.map((r) => ({ ...r })));
  },
  get(id: string): Promise<RoleResponse> {
    const r = ROLES.find((x) => x.id === id || x.code === id);
    if (!r) throw new Error(`Mock: vai trò ${id} không tồn tại`);
    return delay({ ...r });
  },
  permissions(): Promise<PermissionResponse[]> {
    return delay(PERMISSIONS.map((p) => ({ ...p })));
  },
  userRoles(userId: string): Promise<UserRolesResponse> {
    return delay(userRolesResponse(userId));
  },
  replaceUserRoles(
    userId: string,
    req: UserRolesRequest
  ): Promise<UserRolesResponse> {
    USER_ROLES[userId] = [...req.roleCodes];
    return delay(userRolesResponse(userId));
  },
};
